import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Search, MoreVertical, LayoutDashboard, List, ChevronDown, UserPlus, Archive, Trash2, X, Filter } from "lucide-react";
import { useRef, useEffect } from "react";
import CreateBoardDialog from "@/components/kanban/CreateBoardDialog";
import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { boardService } from "@/lib/boardService";
import { useWorkspace } from "@/context/WorkspaceContext";
import { toast } from "sonner";
import { MobileSubMenuHeader } from "@/components/layout";
import { navigationItems } from "@/lib/navigation";

type BoardData = {
  id: string;
  title: string;
  description: string;
  background: {
    type: "gradient" | "color" | "image";
    value: string;
    overlay?: string;
  };
  createdAt: string;
  slug: string;
  status?: 'archived' | 'active';
  teamMemberId?: string;
  columns?: any;
  tasks?: any;
};

const BoardCover = ({ background }: { background: BoardData['background'] }) => {
  let bgStyle: React.CSSProperties = {};

  if (background.type === "color") {
    bgStyle.backgroundColor = background.value;
  } else if (background.type === "gradient") {
    bgStyle.backgroundImage = background.value;
  } else if (background.type === "image") {
    bgStyle.backgroundImage = `url(${background.value})`;
    bgStyle.backgroundSize = "cover";
    bgStyle.backgroundPosition = "center";
  }

  return (
    <div
      className="rounded-lg relative h-52 flex items-center justify-center"
      style={bgStyle}
    >
      {background.type === "image" && background.overlay && (
        <div
          className="absolute inset-0 rounded-t-lg"
          style={{ backgroundColor: background.overlay }}
        />
      )}
    </div>
  );
};

const getActiveTasksCount = (board: any): number => {
  // Return the count from the database (active_tasks_count column)
  return board.active_tasks_count ?? 0;
};

interface TasksProps {
  isGlobalDarkMode?: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

export default function Tasks({ isGlobalDarkMode = true, onMobileSubMenuToggle, isMobile = false }: TasksProps) {
  const [boards, setBoards] = React.useState<BoardData[]>([]);
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [viewMode, setViewMode] = React.useState<'grid' | 'list'>('grid');
  const [, setLoading] = React.useState(true);
  const { currentWorkspace } = useWorkspace();
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);


  // Load boards from database
  React.useEffect(() => {
    const loadBoards = async () => {
      if (!currentWorkspace) {
        setBoards([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await boardService.getBoardsByWorkspace(currentWorkspace.id);
        const boardsData = data.map(board => ({
          id: board.id,
          title: board.title,
          description: board.description,
          background: board.background,
          createdAt: board.created_at,
          slug: board.slug,
          columns: board.columns,
          tasks: board.tasks,
          active_tasks_count: board.active_tasks_count || 0, // Use count from database
        }));
        setBoards(boardsData);
      } catch (error) {
        console.error('Error loading boards:', error);
        toast.error('Failed to load boards');
      } finally {
        setLoading(false);
      }
    };

    loadBoards();
  }, [currentWorkspace]);

  const handleBoardCreated = async () => {
    if (!currentWorkspace) return;

    try {
      const data = await boardService.getBoardsByWorkspace(currentWorkspace.id);
      const boardsData = data.map(board => ({
        id: board.id,
        title: board.title,
        description: board.description,
        background: board.background,
        createdAt: board.created_at,
        slug: board.slug,
        columns: board.columns,
        tasks: board.tasks,
        active_tasks_count: board.active_tasks_count || 0, // Use count from database
      }));
      setBoards(boardsData);
    } catch (error) {
      console.error('Error refreshing boards:', error);
    }
  };

  const handleAssignTeamMember = () => {
    // Placeholder: integrate your team selection modal here
    alert('Team member assignment will open here');
    setOpenMenuId(null);
  };

  const handleArchiveBoard = async (boardId: string) => {
    try {
      const updated = boards.map(b => b.id === boardId ? { ...b, status: 'archived' as const } : b);
      setBoards(updated);
      setOpenMenuId(null);
      toast.success('Board archived');
    } catch (error) {
      console.error('Error archiving board:', error);
      toast.error('Failed to archive board');
    }
  };

  const handleDeleteBoard = async (boardId: string) => {
    try {
      await boardService.deleteBoard(boardId);
      const updated = boards.filter(b => b.id !== boardId);
      setBoards(updated);
      setOpenMenuId(null);
      toast.success('Board deleted');
    } catch (error) {
      console.error('Error deleting board:', error);
      toast.error('Failed to delete board');
    }
  };

  // Task board-specific filters (previous component behavior)
  const [searchQuery, setSearchQuery] = React.useState("");
  const [sortBy, setSortBy] = React.useState<'none' | 'recent'>("none");
  const [filterBy, setFilterBy] = React.useState<'all' | 'archived'>("all");
  const [showSortMenu, setShowSortMenu] = React.useState(false);
  const [showFilterMenu, setShowFilterMenu] = React.useState(false);

  const [searchOpen, setSearchOpen] = React.useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  const handleSortChange = (opt: typeof sortBy) => {
    setSortBy(opt);
    setShowSortMenu(false);
  };

  const handleFilterChange = (opt: typeof filterBy) => {
    setFilterBy(opt);
    setShowFilterMenu(false);
  };

  const filteredBoards = React.useMemo(() => {
    let list = [...boards];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((b) => b.title.toLowerCase().includes(q));
    }
    if ((filterBy as string) === 'archived') {
      // Support future archived flag; keeps behavior of old component if not present
      list = list.filter((b: any) => b.status === 'archived');
    }
    if (sortBy === 'recent') {
      list = list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    return list;
  }, [boards, searchQuery, sortBy, filterBy]);

  const handleMobileMenuToggle = () => {
    const boardsMenuItem = navigationItems.find(item => item.id === 'boards');
    if (boardsMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('boards', boardsMenuItem.subItems, boardsMenuItem.label);
    }
  };

  const handleMobileAdd = () => {
    setShowCreateDialog(true);
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Task Boards | Organized project boards</title>
        <meta name="description" content="Browse task boards. Create, view, and manage all boards in one place." />
        <link rel="canonical" href="/task-board" />
      </Helmet>

      {/* Mobile Sub-Menu Header */}
      {isMobile && (
        <>
          <MobileSubMenuHeader
            onMenuClick={handleMobileMenuToggle}
            onAddClick={handleMobileAdd}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search boards..."
            showSearch={true}
            showAdd={true}
            isDarkMode={isGlobalDarkMode}
          />
          <CreateBoardDialog
            open={showCreateDialog}
            onOpenChange={(open) => {
              setShowCreateDialog(open);
              if (!open) {
                // Refresh boards when dialog closes
                handleBoardCreated();
              }
            }}
            hideTrigger={true}
            onBoardCreated={handleBoardCreated}
          />
        </>
      )}

      {/* Main Content Area - Header and Grid merged in one container matching Brand Dashboard */}
      <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
        {/* Header Section with Search, Filter, Sort and View Toggle */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
          <div className="flex items-center space-x-4">
            <h1 className="text-3xl font-bold text-foreground">Task Boards</h1>
          </div>

          <div className={`flex flex-wrap items-center gap-2 ${isMobile ? 'hidden' : ''}`}>

            {/* Search — icon-only by default, expands on click */}
            <div className="relative flex items-center h-10">
              <AnimatePresence initial={false}>
                {searchOpen ? (
                  <motion.div
                    key="search-open"
                    className="relative flex items-center h-10"
                    initial={{ width: 40, opacity: 0.6 }}
                    animate={{ width: 256, opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search boards..."
                      onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                      className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none"
                    />
                    <button
                      onMouseDown={(e) => { e.preventDefault(); handleSearchClose(); }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center transition-colors text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="search-icon"
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Search className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* View Toggle — animated sliding pill */}
            <div className="relative flex items-center rounded-lg border border-border p-1 bg-card h-10">
              <motion.div
                className="absolute top-1 bottom-1 rounded-sm bg-background shadow-sm"
                initial={false}
                animate={{
                  left: viewMode === 'grid' ? '4px' : 'calc(50%)',
                  width: 'calc(50% - 4px)',
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 35 }}
              />
              <button
                onClick={() => setViewMode('grid')}
                className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'grid' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <LayoutDashboard className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'list' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Filter: All Boards / Archived */}
            <div className="relative h-10">
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className={`flex items-center space-x-2 px-4 h-full rounded-lg border transition-colors ${filterBy !== 'all'
                  ? 'bg-background shadow-sm text-foreground border-transparent'
                  : 'bg-card border-border text-foreground hover:bg-muted'
                  }`}
              >
                <span className="text-sm font-medium whitespace-nowrap">{filterBy === 'all' ? 'All Boards' : 'Archived'}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showFilterMenu && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setShowFilterMenu(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                    >
                      {(['all', 'archived'] as const).map((opt, idx, arr) => (
                        <button
                          key={opt}
                          onClick={() => handleFilterChange(opt)}
                          className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${filterBy === opt
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-foreground hover:bg-accent'
                            }`}
                        >
                          <span>{opt === 'all' ? 'All Boards' : 'Archived'}</span>
                          {filterBy === opt && <div className="w-2 h-2 bg-current rounded-full" />}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* Sort: None / Recent */}
            <div className="relative h-10">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className={`p-2.5 h-full rounded-lg border transition-colors ${sortBy !== 'none'
                  ? 'bg-background shadow-sm text-foreground border-transparent'
                  : 'bg-card border-border text-foreground hover:bg-muted'
                  }`}
                title="Sort boards"
              >
                <Filter className="w-4 h-4" />
              </button>

              <AnimatePresence>
                {showSortMenu && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setShowSortMenu(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full right-0 mt-2 rounded-xl border bg-popover border-border shadow-lg min-w-[180px] p-1.5 z-40"
                    >
                      {(['none', 'recent'] as const).map((opt, idx, arr) => (
                        <button
                          key={opt}
                          onClick={() => handleSortChange(opt)}
                          className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between rounded-lg ${sortBy === opt
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-foreground hover:bg-accent'
                            }`}
                        >
                          <span>{opt === 'none' ? 'None' : 'Recent'}</span>
                          {sortBy === opt && <div className="w-2 h-2 bg-current rounded-full" />}
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <CreateBoardDialog onBoardCreated={handleBoardCreated} />
          </div>
        </div>

        {/* Boards grid/list */}
        <section className={viewMode === 'grid' ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3" : "space-y-4"}>
          {filteredBoards.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground mb-4">No boards created yet</p>
              <CreateBoardDialog onBoardCreated={handleBoardCreated} />
            </div>
          ) : (
            filteredBoards.map((board) => (
              <BoardCard
                key={board.id}
                board={board}
                viewMode={viewMode}
                openMenuId={openMenuId}
                setOpenMenuId={setOpenMenuId}
                handleAssignTeamMember={handleAssignTeamMember}
                handleArchiveBoard={handleArchiveBoard}
                handleDeleteBoard={handleDeleteBoard}
                getActiveTasksCount={getActiveTasksCount}
                currentWorkspaceId={currentWorkspace?.id}
              />
            ))
          )}
        </section>
      </div>
    </div>
  );
}

// Separate BoardCard component for grid/list rendering
interface BoardCardProps {
  board: BoardData;
  viewMode: 'grid' | 'list';
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
  handleAssignTeamMember: () => void;
  handleArchiveBoard: (boardId: string) => void;
  handleDeleteBoard: (boardId: string) => void;
  getActiveTasksCount: (board: BoardData) => number;
  currentWorkspaceId?: string;
}

const BoardCard: React.FC<BoardCardProps> = ({
  board,
  viewMode,
  openMenuId,
  setOpenMenuId,
  handleAssignTeamMember,
  handleArchiveBoard,
  handleDeleteBoard,
  getActiveTasksCount,
  currentWorkspaceId
}) => {
  const handlePrefetch = () => {
    if (currentWorkspaceId) {
      boardService.prefetchBoardData(currentWorkspaceId, board.slug);
    }
  };

  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onMouseEnter={handlePrefetch}
        className="rounded-2xl bg-card text-card-foreground border border-border shadow-sm p-6 transition-all duration-200 hover:shadow-md"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to={`/task-board/${board.slug}`} className="flex items-center space-x-4">
              <div
                className="w-16 h-16 rounded-md flex items-center justify-center relative overflow-hidden"
                style={{
                  backgroundColor: board.background.type === 'color' ? board.background.value : 'var(--workspace-accent, #3B82F6)',
                  backgroundImage: board.background.type === 'gradient' ? board.background.value :
                    board.background.type === 'image' ? `url(${board.background.value})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <span className="text-white font-bold text-lg">
                  {board.title.charAt(0)}
                </span>
              </div>

              <div>
                <h4 className="text-xl font-semibold text-foreground">{board.title}</h4>
                <p className="text-sm text-muted-foreground">
                  <span className={getActiveTasksCount(board) > 0 ? 'text-green-500' : ''}>
                    {getActiveTasksCount(board)} active tasks
                  </span>
                  {' • '}
                  {new Date(board.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </Link>
          </div>

          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(openMenuId === board.id ? null : board.id);
              }}
              className="p-2 transition-colors text-muted-foreground hover:text-foreground"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {openMenuId === board.id && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setOpenMenuId(null)} />
                <div className="absolute top-8 right-0 z-40 rounded-xl border border-border bg-popover shadow-lg min-w-[170px] p-1.5">
                  <button
                    onClick={handleAssignTeamMember}
                    className="w-full text-left px-3 py-2 text-sm transition-colors text-foreground hover:bg-accent rounded-lg flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Assign</span>
                  </button>
                  <button
                    onClick={() => handleArchiveBoard(board.id)}
                    className="w-full text-left px-3 py-2 text-sm transition-colors text-foreground hover:bg-accent rounded-lg flex items-center gap-2"
                  >
                    <Archive className="w-4 h-4" />
                    <span>Archive</span>
                  </button>
                  <div className="my-1 h-px bg-border" />
                  <button
                    onClick={() => handleDeleteBoard(board.id)}
                    className="w-full text-left px-3 py-2 text-sm transition-colors text-destructive hover:bg-destructive/10 rounded-lg flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // Grid view (original card design)
  return (
    <div
      onMouseEnter={handlePrefetch}
      className="group rounded-2xl bg-card text-card-foreground border border-border shadow-sm transition-all duration-200 hover:shadow-md overflow-hidden">
      <Link to={`/task-board/${board.slug}`} aria-label={`Open ${board.title} board`}>
        <BoardCover background={board.background} />
      </Link>
      <div className="py-5 px-6 relative">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-lg font-semibold leading-none truncate text-foreground">{board.title}</div>
            <div className="mt-2 text-sm text-muted-foreground">
              <span className={getActiveTasksCount(board) > 0 ? 'text-green-500' : ''}>
                {getActiveTasksCount(board)} active tasks
              </span>
            </div>
          </div>
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(openMenuId === board.id ? null : board.id);
              }}
              className="p-2 rounded-full transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
              aria-label="Board actions"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {openMenuId === board.id && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setOpenMenuId(null)} />
                <div className="absolute bottom-10 right-0 z-40 rounded-xl border border-border bg-popover shadow-lg min-w-[170px] p-1.5">
                  <button
                    onClick={handleAssignTeamMember}
                    className="w-full text-left px-3 py-2 text-sm transition-colors text-foreground hover:bg-accent rounded-lg flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Assign</span>
                  </button>
                  <button
                    onClick={() => handleArchiveBoard(board.id)}
                    className="w-full text-left px-3 py-2 text-sm transition-colors text-foreground hover:bg-accent rounded-lg flex items-center gap-2"
                  >
                    <Archive className="w-4 h-4" />
                    <span>Archive</span>
                  </button>
                  <div className="my-1 h-px bg-border" />
                  <button
                    onClick={() => handleDeleteBoard(board.id)}
                    className="w-full text-left px-3 py-2 text-sm transition-colors text-destructive hover:bg-destructive/10 rounded-lg flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};